import * as THREE from 'three'
import { reefGeometry, type ReefAsset } from '../game/world/reef-assets'
import { reefMaterials } from '../game/world/reef-materials'

/** Caller owns both the returned buffer and the envelope. Gameplay's cached GLB
 * is never mutated: authored stone is fitted inside the prior staging bounds. */
export function fittedDioramaRock(seed: number, envelope: THREE.BufferGeometry): THREE.BufferGeometry {
  const name = `fractured-stone-${Math.abs(Math.floor(seed)) % 8}` as ReefAsset
  const source = reefGeometry(name)
  if (!source) return envelope
  envelope.computeBoundingBox()
  const bounds = envelope.boundingBox!
  const size = bounds.getSize(new THREE.Vector3())
  const centre = bounds.getCenter(new THREE.Vector3())
  const geometry = source.clone()
  geometry.computeBoundingBox()
  const sourceSize = geometry.boundingBox!.getSize(new THREE.Vector3())
  const sourceCentre = geometry.boundingBox!.getCenter(new THREE.Vector3())
  geometry.translate(-sourceCentre.x, -sourceCentre.y, -sourceCentre.z)
  geometry.scale(size.x / sourceSize.x, size.y / sourceSize.y, size.z / sourceSize.z)
  // Weather the fitted masonry silhouette into a reef boulder. Preserve the
  // authored fracture detail while pulling box corners onto an eroded envelope.
  const vertices = geometry.attributes.position
  for (let i = 0; i < vertices.count; i++) {
    const x = vertices.getX(i) / (size.x * 0.5)
    const y = vertices.getY(i) / (size.y * 0.5)
    const z = vertices.getZ(i) / (size.z * 0.5)
    const radius = Math.sqrt(x * x + y * y + z * z)
    const rounding = 0.08 + 0.92 / Math.max(0.35, radius)
    const erosion = 0.92 + Math.sin(y * 4.1 + z * 2.7 + seed) * 0.085 + Math.cos(x * 3.8 - z * 2.1 + seed) * 0.055
    vertices.setXYZ(i, vertices.getX(i) * rounding * erosion,
      vertices.getY(i) * rounding, vertices.getZ(i) * rounding * erosion)
  }
  geometry.computeVertexNormals()
  geometry.translate(centre.x, centre.y, centre.z)
  envelope.dispose()
  return geometry
}

/** UVs use local world units after static transforms have been baked. A two-unit
 * sand tile therefore stays two units wide on the floor, bridge banks and shelves. */
export function projectDioramaTerrainUVs(geometry: THREE.BufferGeometry, tileSize = 2): THREE.BufferGeometry {
  const position = geometry.attributes.position
  if (!geometry.attributes.normal) geometry.computeVertexNormals()
  const normal = geometry.attributes.normal
  const uv = new Float32Array(position.count * 2)
  for (let i = 0; i < position.count; i++) {
    const nx = Math.abs(normal.getX(i)), ny = Math.abs(normal.getY(i)), nz = Math.abs(normal.getZ(i))
    if (ny >= nx && ny >= nz) {
      uv[i * 2] = position.getX(i) / tileSize
      uv[i * 2 + 1] = position.getZ(i) / tileSize
    } else if (nx >= nz) {
      uv[i * 2] = position.getZ(i) / tileSize
      uv[i * 2 + 1] = position.getY(i) / tileSize
    } else {
      uv[i * 2] = position.getX(i) / tileSize
      uv[i * 2 + 1] = position.getY(i) / tileSize
    }
  }
  geometry.setAttribute('uv', new THREE.BufferAttribute(uv, 2))
  return geometry
}

/** Texture objects are copied so stage repeat/offset and disposal cannot affect
 * gameplay. Pixel sources remain shared; this introduces no duplicate image decode. */
export function cloneDioramaTerrainMaterial(kind: 'sand' | 'limestone', tint?: number): {
  material: THREE.MeshStandardMaterial
  textures: THREE.Texture[]
} | null {
  const source = reefMaterials()[kind]
  if (!source.map) return null
  const material = source.clone()
  material.name = `DioramaAuthored_${kind}`
  if (tint !== undefined) material.color.setHex(tint)
  const textures: THREE.Texture[] = []
  for (const key of ['map', 'normalMap', 'roughnessMap', 'bumpMap'] as const) {
    if (!source[key]) continue
    const texture = source[key]!.clone()
    texture.repeat.set(1, 1)
    texture.offset.set(0, 0)
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping
    texture.needsUpdate = true
    material[key] = texture
    textures.push(texture)
  }
  return { material, textures }
}
